import { Add, Alert, Button, FormCard, theme } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { ConsentFormSnapshot } from 'screens/CarePlans/hooks/useCarePlanPopulated'
import {
  type ConsentForm,
  useConsentForms
} from 'screens/CarePlans/hooks/useConsentForms'
import styled from 'styled-components'
import type { ConsentFormData } from './ConsentFormItem'
import ConsentFormsList from './ConsentFormsList'
import LinkConsentModal from './LinkConsentModal'

interface ConsentSectionProps {
  residentId: string
  consentForms?: ConsentFormData[]
  consentFormsSnapshot?: ConsentFormSnapshot[]
  value?: string[]
  disabled?: boolean
  onChange?: (consentIds: string[]) => void
  isRevision?: boolean
  disableItemNavigation?: boolean
}

const ConsentSection: React.FC<ConsentSectionProps> = ({
  residentId,
  consentForms,
  consentFormsSnapshot = [],
  value = [],
  disabled = false,
  onChange,
  isRevision = false,
  disableItemNavigation = false
}) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)
  const [isModalVisible, setIsModalVisible] = useState(false)

  const { data: allConsentForms } = useConsentForms({
    residentId,
    enabled: !!residentId && !isRevision && !consentForms
  })

  // Determine which data to display based on isRevision flag
  const displayedForms = isRevision
    ? consentFormsSnapshot.map((snapshot) => ({
        id: snapshot.id,
        title: snapshot.title || 'Untitled',
        isArchived: snapshot.isArchived || false,
        createdOn: snapshot.createdOn
          ? new Date(snapshot.createdOn).toISOString()
          : '',
        consentStatus: snapshot.consentStatus || 'no-status',
        signedOn: snapshot.signedOn
          ? new Date(snapshot.signedOn).toISOString()
          : undefined,
        signedBy: snapshot.signedBy,
        createdBy: snapshot.createdBy
      }))
    : consentForms ||
      (allConsentForms ?? []).filter((form: ConsentForm) =>
        value.includes(form.id)
      )

  const handleOpenModal = () => {
    setIsModalVisible(true)
  }

  const handleLinkConsents = (selectedIds: string[]) => {
    onChange?.(selectedIds)
  }

  const hasConsentForms = displayedForms.length > 0
  const hasNoSnapshotData = isRevision && consentFormsSnapshot.length === 0

  return (
    <>
      <FormCard
        title={
          <TitleContainer>
            <FormCardHeading level={3}>
              {t(carePlansConstants.CONSENT_SECTION_TITLE)}
            </FormCardHeading>
            {!isRevision && (
              <DescriptionText>
                {t(
                  disabled
                    ? carePlansConstants.CONSENT_SECTION_DESCRIPTION_VIEW
                    : carePlansConstants.CONSENT_SECTION_DESCRIPTION
                )}{' '}
                <StyledLink to={`/residents/${residentId}/consent`}>
                  {t(carePlansConstants.CONSENT_MANAGEMENT_LINK)}
                </StyledLink>
              </DescriptionText>
            )}
            {isRevision && (
              <DescriptionText>
                Historical consent forms linked at the time of this review
              </DescriptionText>
            )}
          </TitleContainer>
        }
        rightChildren={
          !disabled &&
          !isRevision && (
            <Button
              type="button"
              variant="text"
              icon={<Add />}
              onClick={handleOpenModal}
            >
              {t(carePlansConstants.LINK_CONSENT_BUTTON)}
            </Button>
          )
        }
      >
        {hasNoSnapshotData ? (
          <Alert
            message="No consent forms were linked at the time of this review"
            type="info"
            showIcon
          />
        ) : hasConsentForms ? (
          <ConsentFormsList
            items={displayedForms}
            residentId={residentId}
            testId="consent-forms-list"
            isSnapshot={isRevision}
            snapshotData={consentFormsSnapshot}
            disableItemNavigation={disableItemNavigation}
          />
        ) : undefined}
      </FormCard>
      <LinkConsentModal
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        residentId={residentId}
        linkedConsentIds={value}
        onConfirm={handleLinkConsents}
      />
    </>
  )
}

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const DescriptionText = styled(Text)`
  color: ${theme.color.neutral.color4};
`

const StyledLink = styled(Link)`
  color: ${theme.color.status.primary};
  text-decoration: underline;

  &:hover {
    color: ${theme.color.status.primary};
    text-decoration: underline;
  }
`

const FormCardHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

export default ConsentSection
